// MARK://dhk8/admin-shell/pages/modules/FiscalCustomerDetailPage
// Ficha "ver-primero" de un cliente fiscal (receptor de CFDI) (Gustavo 2026-06-23).
// Header con nombre/razón social + RFC + pills (uso CFDI / régimen) + KPIs (facturas,
// timbradas, total facturado) + Tabs (General, Facturas, Historial). El tab General se
// ve primero; "Editar" abre el SectionedSheet con el MISMO formulario que el modal de
// la lista. No existe getFiscalCustomer(id) en el client → se resuelve por
// listFiscalCustomers() + find(id) (como ProductDetailPage hace con las recetas). Las
// facturas se traen con listInvoices() y se filtran best-effort por este cliente
// (customer === id ó rfc coincide), porque Invoice.customer = customerId desde el grafo
// y = razón social en data mock/legacy.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Trash2 } from 'lucide-react'

import { listFiscalCustomers, updateFiscalCustomer, deleteFiscalCustomer, listInvoices } from '../../lib/clients/invoices.js'
import type { FiscalCustomer } from '../../lib/mock-data.js'
import { REGIMES } from '../../lib/clients/fiscal.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

type Tab = 'general' | 'facturas' | 'historial'

const USOS = ['G01', 'G02', 'G03', 'I01', 'I04', 'P01', 'CP01', 'S01']
const STATUS_TONE: Record<string, 'ok' | 'danger' | 'warn'> = { STAMPED: 'ok', CANCELED: 'danger', PENDING: 'warn' }
const STATUS_LABEL: Record<string, string> = { STAMPED: 'Timbrada', CANCELED: 'Cancelada', PENDING: 'Pendiente' }
const regimeName = (code: string) => REGIMES.find((r) => r.code === code)?.name ?? null

export default function FiscalCustomerDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const confirm = useConfirm()

  // No hay getFiscalCustomer(id): se trae la lista y se filtra por id (patrón recetas).
  const customers = useAsync((s) => listFiscalCustomers(s))
  const invoices = useAsync((s) => listInvoices(s))

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FiscalCustomer | null>(null)
  const [busy, setBusy] = useState(false)

  const c = useMemo(() => (customers.data ?? []).find((x) => x.id === id) ?? null, [customers.data, id])

  // Facturas de este cliente (best-effort): el grafo guarda customer = customerId, la
  // data mock/legacy guarda customer = razón social → matcheamos por id ó por RFC.
  const customerInvoices = useMemo(() => {
    if (!c) return []
    const rfc = c.rfc.trim().toUpperCase()
    return (invoices.data ?? []).filter((inv) =>
      inv.customer === c.id || (rfc.length > 0 && inv.rfc.trim().toUpperCase() === rfc))
  }, [invoices.data, c])

  const stampedCount = useMemo(() => customerInvoices.filter((i) => i.status === 'STAMPED').length, [customerInvoices])
  const billedCents = useMemo(() => customerInvoices.filter((i) => i.status !== 'CANCELED').reduce((sum, i) => sum + i.totalCents, 0), [customerInvoices])

  function openEdit() { if (c) { setForm({ ...c }); setEditing(true) } }
  async function save() {
    if (!form) return
    setBusy(true)
    try { await updateFiscalCustomer(form); toast('Cliente fiscal actualizado', 'success'); setEditing(false); customers.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }

  async function remove() {
    if (!c) return
    const ok = await confirm({
      title: '¿Eliminar cliente fiscal?',
      message: `Se eliminará a "${c.name || c.rfc}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try { await deleteFiscalCustomer(c.id); toast('Cliente fiscal eliminado', 'success'); navigate('/customers/fiscal') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el cliente fiscal.', 'danger') }
  }

  const kpis = useMemo(() => {
    if (!c) return []
    return [
      { label: 'Facturas', value: customerInvoices.length },
      { label: 'Timbradas', value: stampedCount, tone: 'ok' as const },
      { label: 'Total facturado', value: mxn(billedCents) },
    ]
  }, [c, customerInvoices.length, stampedCount, billedCents])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/customers/fiscal')} aria-label="Volver a clientes fiscales"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Cliente fiscal</h1><p className="adm-page__sub">Facturas · receptor de CFDI</p></div>
        </div>
      </div>

      {asyncState(customers.loading, customers.error, customers.reload)}
      {!customers.loading && !c && <div className="adm-card adm-card--pad"><p className="adm-muted">No se encontró el cliente fiscal.</p></div>}

      {c && (
        <EntityDetailPage
          title={c.name || 'Sin razón social'}
          subtitle={c.rfc ? `RFC ${c.rfc}` : 'Sin RFC'}
          pills={<>
            <StatusPill tone="info" label={`Uso ${c.usoCfdi || '—'}`} />
            {c.regimeCode && <StatusPill tone="neutral" label={`Rég. ${c.regimeCode}`} />}
          </>}
          kpis={<SummaryStrip items={kpis} />}
          actions={<>
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={remove}><Trash2 size={15} /> Eliminar</button>
          </>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'facturas', label: 'Facturas', count: customerInvoices.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del cliente fiscal"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre / Razón social">{c.name || <span className="adm-muted">—</span>}</Field>
              <Field label="RFC"><span className="adm-mono">{c.rfc || '—'}</span></Field>
              <Field label="Uso de CFDI"><span className="adm-mono">{c.usoCfdi || '—'}</span></Field>
              <Field label="Régimen fiscal">
                {c.regimeCode ? <><span className="adm-mono">{c.regimeCode}</span>{regimeName(c.regimeCode) ? ` — ${regimeName(c.regimeCode)}` : ''}</> : <span className="adm-muted">—</span>}
              </Field>
              <Field label="Correo">{c.email || <span className="adm-muted">Sin correo</span>}</Field>
            </div>
          )}

          {tab === 'facturas' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(invoices.loading, invoices.error, invoices.reload)}
              <div className="adm-list">
                {!invoices.loading && customerInvoices.length === 0 && (
                  <div className="adm-empty">Este cliente no tiene facturas emitidas.</div>
                )}
                {customerInvoices.map((inv) => (
                  <button key={inv.id} type="button" className="adm-row" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <div className="adm-row__main">
                      <div className="adm-row__title">{inv.folio}</div>
                      <div className="adm-row__meta">{[inv.date, inv.uuid && `UUID ${inv.uuid}`].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="adm-row__tail">
                      <StatusPill tone={STATUS_TONE[inv.status] ?? 'neutral'} label={STATUS_LABEL[inv.status] ?? inv.status} />
                      <span className="adm-mono">{mxn(inv.totalCents)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={c.id} />}
        </EntityDetailPage>
      )}

      {form && (
        <SectionedSheet
          open={editing}
          title="Editar cliente fiscal"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim() || !form.rfc.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Identidad fiscal">
            <label className="adm-field"><span className="adm-field__label">Nombre / Razón social</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">RFC</span><input className="adm-input" value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} /></label>
            <label className="adm-field"><span className="adm-field__label">Correo</span><input className="adm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          </FormSection>
          <FormSection title="Datos CFDI">
            <label className="adm-field"><span className="adm-field__label">Régimen fiscal</span><select className="adm-select" value={form.regimeCode} onChange={(e) => setForm({ ...form, regimeCode: e.target.value })}>{REGIMES.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</select></label>
            <label className="adm-field"><span className="adm-field__label">Uso de CFDI</span><select className="adm-select" value={form.usoCfdi} onChange={(e) => setForm({ ...form, usoCfdi: e.target.value })}>{USOS.map((u) => <option key={u} value={u}>{u}</option>)}</select></label>
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
